import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Typography } from "antd";
import { createClient } from "graphql-sse";
import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useAuthToken } from "@/auth/use-auth-token";
import { useMe } from "@/common/artemis-operations/members";
import { useRouteOrgSlug } from "@/common/artemis-operations/organization";
import {
	OnChangeDocument,
	type OnChangeSubscription,
} from "@/common/artemis-sdk/graphql";
import { useNotify } from "@/common/use-notify";

const POPUPS_STORAGE_KEY = "realtime.popups";

// Dev/testing escape hatch: also popup for events caused by the current user
// (normally suppressed). Lets scripts/simulate-changes.sh demo notifications
// with your own token. On in .env.local, unset in gamma/prod.
const notifySelf = () => import.meta.env.VITE_REALTIME_NOTIFY_SELF === "true";

/**
 * Backend ChangeEvent.entityType → React Query keys to invalidate. `lists` are key prefixes
 * (some lists are cached under more than one key, or keyed by project rather than workspace,
 * so we invalidate the whole prefix — only active queries actually refetch); `item` is the
 * single-entity key, `[item, entityId]`.
 */
const ENTITY_KEYS: Record<string, { lists: string[]; item: string }> = {
	Task: { lists: ["tasks"], item: "task" },
	Approval: { lists: ["approvals", "approvals-paged"], item: "approval" },
	Project: { lists: ["projects"], item: "project" },
	Environment: { lists: ["environments"], item: "environment" },
	Report: { lists: ["reports"], item: "report" },
	Job: { lists: ["jobs"], item: "job" },
	Workspace: { lists: ["workspaces"], item: "workspace" },
	Agent: { lists: ["agents"], item: "agent" },
};

/**
 * When the user is away — tab hidden OR window unfocused (e.g. Cmd-Tabbed to another
 * app, where the tab still counts as visible) — surface the event through the OS
 * instead of an in-page toast.
 */
function osNotify(title: string, body?: string): boolean {
	const away = document.hidden || !document.hasFocus();
	if (
		!away ||
		!("Notification" in window) ||
		Notification.permission !== "granted"
	) {
		return false;
	}
	new Notification(title, { body, icon: "/logo192.png" });
	return true;
}

type RealtimeContext = {
	/** Whether realtime events also surface as notification popups (background refresh always happens). */
	popupsEnabled: boolean;
	setPopupsEnabled: (enabled: boolean) => void;
};

const RealtimeContext = createContext<RealtimeContext | undefined>(undefined);

/**
 * Central realtime pipe: one GraphQL-over-SSE subscription to the org's change events
 * (authenticated with the same Bearer token as every other request). Every event silently
 * invalidates the matching React Query caches so open pages refresh without reload; when
 * popups are enabled, events from other actors additionally show one toast.
 */
export function RealtimeProvider({ children }: { children: ReactNode }) {
	const { getToken } = useAuthToken();
	const queryClient = useQueryClient();
	const notify = useNotify();
	const me = useMe(); // reads the cache populated by AuthenticatedLayout; no extra fetch
	const [popupsEnabled, setPopupsEnabledState] = useState(
		() => localStorage.getItem(POPUPS_STORAGE_KEY) !== "off",
	);

	const navigate = useNavigate();
	const orgSlug = useRouteOrgSlug();

	// Navigates to the changed entity's page; deleted entities have no page to open.
	const openEntity = (evt: OnChangeSubscription["onChange"]) => {
		if (evt.action === "Deleted" || !orgSlug) return;
		const base = { orgSlug, workspaceId: evt.workspaceId ?? "" };
		switch (evt.entityType) {
			case "Task":
				return navigate({
					to: "/$orgSlug/workspaces/$workspaceId/tasks/$taskId",
					params: { ...base, taskId: evt.entityId },
				});
			case "Project":
				return navigate({
					to: "/$orgSlug/workspaces/$workspaceId/projects/$projectId",
					params: { ...base, projectId: evt.entityId },
				});
			case "Environment":
				return navigate({
					to: "/$orgSlug/workspaces/$workspaceId/environments/$environmentId",
					params: { ...base, environmentId: evt.entityId },
				});
			case "Report":
				return navigate({
					to: "/$orgSlug/workspaces/$workspaceId/reports/$reportId",
					params: { ...base, reportId: evt.entityId },
				});
			case "Job":
				return navigate({
					to: "/$orgSlug/workspaces/$workspaceId/jobs/$jobId",
					params: { ...base, jobId: evt.entityId },
				});
			case "Approval": // no detail page — land on the workspace's approvals list
				return navigate({
					to: "/$orgSlug/workspaces/$workspaceId/approvals",
					params: base,
					search: { search: undefined },
				});
			case "Workspace":
				return navigate({
					to: "/$orgSlug/workspaces/$workspaceId",
					params: { orgSlug, workspaceId: evt.entityId },
				});
			case "Agent":
				return navigate({
					to: "/$orgSlug/workspaces/$workspaceId/agents/$agentId",
					params: { ...base, agentId: evt.entityId },
				});
		}
	};

	// The SSE effect must not tear down/reconnect when settings or identity change mid-stream.
	const stateRef = useRef({
		popupsEnabled,
		myUserId: me.data?.id,
		notify,
		openEntity,
	});
	stateRef.current = {
		popupsEnabled,
		myUserId: me.data?.id,
		notify,
		openEntity,
	};

	// Popups default to on, so the toggle (the other permission-request point) may never be
	// touched — ask once on load too, or OS notifications would silently never fire.
	// biome-ignore lint/correctness/useExhaustiveDependencies: once per mount is enough
	useEffect(() => {
		if (
			popupsEnabled &&
			"Notification" in window &&
			Notification.permission === "default"
		) {
			Notification.requestPermission();
		}
	}, []);

	useEffect(() => {
		const client = createClient({
			url: import.meta.env.VITE_PLATFORM_API_URL,
			headers: async (): Promise<Record<string, string>> => {
				const token = await getToken();
				return token ? { Authorization: `Bearer ${token}` } : {};
			},
			retryAttempts: Infinity,
			on: {
				// Events aren't replayed after a broken connection: refetch everything we stream.
				connected: (reconnected) => {
					if (reconnected) {
						for (const { lists } of Object.values(ENTITY_KEYS)) {
							for (const list of lists) {
								queryClient.invalidateQueries({ queryKey: [list] });
							}
						}
					}
				},
			},
		});

		const unsubscribe = client.subscribe<OnChangeSubscription>(
			{ query: OnChangeDocument.toString() },
			{
				next: (result) => {
					const evt = result.data?.onChange;
					if (!evt) return;

					const keys = ENTITY_KEYS[evt.entityType];
					if (!keys) return;
					for (const list of keys.lists) {
						queryClient.invalidateQueries({ queryKey: [list] });
					}
					queryClient.invalidateQueries({
						queryKey: [keys.item, evt.entityId],
					});

					const { popupsEnabled, myUserId, notify, openEntity } =
						stateRef.current;
					// Creates/deletes are always news; plain updates only when the status
					// actually changed (worklog appends etc. refresh silently).
					const popupWorthy = evt.action !== "Updated" || evt.statusChanged;
					if (
						popupsEnabled &&
						popupWorthy &&
						(notifySelf() || !evt.actorUserId || evt.actorUserId !== myUserId)
					) {
						// Reads as a sentence: "Task tsk_123 updated [Working]".
						const action = evt.action.toLowerCase();
						if (
							!osNotify(
								`${evt.entityType} ${evt.entityId} ${action}`,
								evt.status ? `Status: ${evt.status}` : undefined,
							)
						) {
							notify.info(
								<>
									{evt.entityType}{" "}
									{evt.action === "Deleted" ? (
										evt.entityId
									) : (
										<Typography.Link onClick={() => openEntity(evt)}>
											{evt.entityId}
										</Typography.Link>
									)}{" "}
									{action}
								</>,
							);
						}
					}
				},
				error: () => {},
				complete: () => {},
			},
		);

		return () => {
			unsubscribe();
			client.dispose();
		};
	}, [getToken, queryClient]);

	const value = useMemo(
		() => ({
			popupsEnabled,
			setPopupsEnabled: (enabled: boolean) => {
				localStorage.setItem(POPUPS_STORAGE_KEY, enabled ? "on" : "off");
				setPopupsEnabledState(enabled);
				// The toggle click is a user gesture, so the browser will show the permission
				// prompt; once granted, hidden-tab events surface as OS notifications.
				if (
					enabled &&
					"Notification" in window &&
					Notification.permission === "default"
				) {
					Notification.requestPermission();
				}
			},
		}),
		[popupsEnabled],
	);

	return (
		<RealtimeContext.Provider value={value}>
			{children}
		</RealtimeContext.Provider>
	);
}

export function useRealtime() {
	const context = useContext(RealtimeContext);
	if (context === undefined) {
		throw new Error("useRealtime must be used within a RealtimeProvider");
	}
	return context;
}
